import { useRef, useState } from "react";
import { Card, Flex, InputRef } from "antd";
import { DollarOutlined, PercentageOutlined, TagOutlined } from "@ant-design/icons";
import MetricCard from "components/MetricCard.component";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { getDiscountCodeAnalytics } from "hooks/queries/analytics";
import { useTableSort } from "hooks/useTableSort";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { DISCOUNT_CODE_STATUS_LABELS, DISCOUNT_CODE_STATUS_TONE, formatCurrency } from "shared/utils";
import { discountCodeRuleLabel } from "shared/utils/loyalty";
import { tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { AnalyticsRange, CustomDayRange, DiscountCodePerformanceRow } from "shared/types";
import PATHS from "shared/constants/paths";
import { getYtdDayRange } from "shared/utils/dayjs";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { useAuth } from "context/AuthContextProvider";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { byNumber } from "shared/reports/columnCatalogue";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { discountCodeSpec } from "pdf/reports/specs/archetype2";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

// Performa Kode Diskon: one row per code, so an admin can see whether each
// code paid off. Lists every code used in the period plus every code that
// could have been used in it, so an unused code reads 0 rather than being
// absent. Open to every Admin, revenue included (Andrew, 2026-09-24).
const DiscountCodePerformancePage = () => {
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive =
    customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const searchInput = useRef<InputRef>(null);
  const { data, isLoading } = getDiscountCodeAnalytics(range, customRange);

  const windowParams = customRange
    ? { from_date: customRange.from, to_date: customRange.to }
    : { range };

  const columns: ReportColumn<DiscountCodePerformanceRow>[] = [
    reportColumn<DiscountCodePerformanceRow>("code", {
      dataIndex: "code",
      search: getColumnSearchProps<DiscountCodePerformanceRow, "code">({
        config: "code",
        searchInput,
      }),
    }),
    reportColumn<DiscountCodePerformanceRow>("discount_rule", {
      key: "discount",
      render: (_: unknown, row) => <span style={tnum}>{discountCodeRuleLabel(row)}</span>,
    }),
    reportColumn<DiscountCodePerformanceRow>("status", {
      // The code's status now, as the Kode Diskon settings page shows it. An
      // on/off switch has no history, so this is not its status in the period.
      dataIndex: "status",
      render: (_: unknown, row) => (
        <StatusBadge
          tone={DISCOUNT_CODE_STATUS_TONE[row.status]}
          label={DISCOUNT_CODE_STATUS_LABELS[row.status]}
        />
      ),
    }),
    reportColumn<DiscountCodePerformanceRow>("uses", {
      dataIndex: "uses",
      sorter: (a, b) => a.uses - b.uses,
      defaultSortOrder: "descend",
    }),
    reportColumn<DiscountCodePerformanceRow>("unique_clients", {
      dataIndex: "clients",
      sorter: (a, b) => a.clients - b.clients,
    }),
    reportColumn<DiscountCodePerformanceRow>("new_clients", {
      dataIndex: "new_clients",
      info: () => translate("analyticsPage.discountCode.col_new_clients_info"),
      sorter: (a, b) => a.new_clients - b.new_clients,
    }),
    reportColumn<DiscountCodePerformanceRow>("total_discount", {
      dataIndex: "discount_given",
      sorter: (a, b) => a.discount_given - b.discount_given,
    }),
    // Pembatasan Admin: the sales the codes discounted are the only revenue on
    // an otherwise marketing report. The server has already nulled the figures;
    // leaving the column out is what makes the table read as if it never had
    // one. layoutReportColumns recomputes the width shares and the scroll
    // threshold from whatever column set it is given, so Kode simply gets
    // wider. Total Diskon above stays: a discount is a cost, not revenue.
    ...(data?.money_hidden
      ? []
      : [
          reportColumn<DiscountCodePerformanceRow>("sales", {
            dataIndex: "revenue",
            sorter: byNumber<DiscountCodePerformanceRow>("revenue"),
          }),
        ]),
  ];

  const pageCount = useReportPageCount(discountCodeSpec, data, sortState);

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.discountCode.report_title")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="performa-kode-diskon"
            params={windowParams}
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(data.table.length, MAX_PDF_ROWS),
                  total: data.table_total,
                },
                fileName: `spott-performa-kode-diskon-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "performa-kode-diskon",
                    studio: authState.business?.name ?? "",
                    data,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>{data?.range_label}</span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker
              value={isYtdActive ? null : customRange}
              onChange={setCustomRange}
            />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>

        <Flex gap={16} wrap="wrap">
          <MetricCard
            icon={<PercentageOutlined />}
            title={translate("analyticsPage.discountCode.kpi_discount_given")}
            information={translate("analyticsPage.discountCode.kpi_discount_given_info")}
            value={formatCurrency(data?.discount_given.value ?? 0)}
          />
          {/* Dropped with the column, never alone: masking only the card would
              leave the figures in the table and the workbook for anyone to sum
              back into the total. */}
          {!data?.money_hidden && (
            <MetricCard
              icon={<DollarOutlined />}
              title={translate("analyticsPage.discountCode.kpi_revenue")}
              information={translate("analyticsPage.columns.sales_info")}
              value={formatCurrency(data?.discounted_revenue?.value ?? 0)}
            />
          )}
          <MetricCard
            icon={<TagOutlined />}
            title={translate("analyticsPage.discountCode.kpi_most_used")}
            value={data?.most_used?.code ?? "-"}
          />
        </Flex>

        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<DiscountCodePerformanceRow>
            serverTotal={{ total: data?.table_total, noun: translate("pdf.noun.kode") }}
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={data?.table ?? []}
            pageMemory="analyticsDiscountCodes"
            onSortChange={onSortChange}
          />
        </Card>
      </Flex>
    </>
  );
};

export default DiscountCodePerformancePage;
